import React, { useCallback, useMemo, useState } from 'react';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import moment from 'moment';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import Datepicker from '../../components/widgets/Datepicker';
import Spinner from '../../components/widgets/Spinner';
import LineChart from '../../components/partials/LineChart';
import HelpMenu from '../../components/molecules/HelpMenu';
import { getUserCurrency } from '../../selectors/user';
import {
    useDefaultDates,
    useNetworkChartData,
    useNetworkSummaryData,
    useRejectionMethodsData,
    useTotalsData,
} from './hooks';
import {
    NETWORK_SUMMARY_COLUMNS,
    REJECTION_METHODS_COLUMNS,
    TOTALS_CONFIG,
    TOTALS_METRIC_COLORS,
    TOTALS_METRIC_DISPLAY_NAMES,
    TOTALS_METRIC_TYPES,
} from '../consts';
import { buildFraudChartData, buildPinnedTopRow, formatValue } from './utils';
import css from './style.css';

// Built once and reused across all dimension-column sorts: case-insensitive, locale-aware
// ordering (vs ag-Grid's default case-sensitive ASCII comparison).
const dimensionCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

const RowShape = PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]));

const ColumnShape = PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['dimension', 'number', 'percentage', 'currency']).isRequired,
});

const TableDataShape = PropTypes.shape({
    results: PropTypes.arrayOf(RowShape),
    total: RowShape,
});

const ChartDataShape = PropTypes.shape({
    dimensions: PropTypes.objectOf(PropTypes.string),
    dimensionsSorted: PropTypes.arrayOf(PropTypes.string),
    metrics: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            display_name: PropTypes.string,
            format: PropTypes.shape({ type: PropTypes.string, precision: PropTypes.number }),
        })
    ),
    orgOptions: PropTypes.shape({ metricsLimit: PropTypes.number }),
});

const MetricCards = ({ data, loading, currencySymbol, translate }) => {
    const totals = data?.total || {};

    return (
        <div className={css.sectionCard}>
            <div className={css.sectionHeader}>{translate('STATIC.PAGES.FRAUD.DASHBOARD.TOTALS')}</div>
            <div className={css.sectionBody}>
                {loading ? (
                    <div className={css.spinnerWrapper}>
                        <Spinner show />
                    </div>
                ) : (
                    <div className={css.metricCardsContainer}>
                        {TOTALS_CONFIG.metrics.map(metricKey => {
                            const type = TOTALS_METRIC_TYPES[metricKey];
                            const color = TOTALS_METRIC_COLORS[metricKey];
                            return (
                                <div key={metricKey} className={css.metricCard} style={{ borderLeftColor: color }}>
                                    <div className={css.metricCardLabel} style={{ color }}>
                                        {TOTALS_METRIC_DISPLAY_NAMES[metricKey]}
                                    </div>
                                    <div
                                        className={classNames(css.metricCardValue, {
                                            [css.metricCardValueEmpty]: !data,
                                        })}
                                    >
                                        {data ? formatValue(totals[metricKey], type, currencySymbol) : '—'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

MetricCards.propTypes = {
    data: TableDataShape,
    loading: PropTypes.bool,
    currencySymbol: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

const DataTable = ({
    title,
    columns,
    data,
    loading,
    currencySymbol,
    translate,
    pageSize = 20,
    naked = false,
    dimensionWidth = 400,
}) => {
    const rawRows = useMemo(() => data?.results || [], [data]);
    const totals = useMemo(() => data?.total || {}, [data]);
    const hasTotals = Object.keys(totals).length > 0;

    const columnDefs = useMemo(
        () =>
            columns.map(col => {
                const isMetric = col.type !== 'dimension';
                return {
                    field: col.key,
                    headerName: col.label,
                    sortable: true,
                    ...(isMetric
                        ? { flex: 1, minWidth: 110, headerClass: css.numericHeader }
                        : {
                              width: dimensionWidth,
                              headerClass: css.dimensionHeader,
                              comparator: (a, b) => dimensionCollator.compare(String(a), String(b)),
                          }),
                    valueFormatter: ({ value }) => formatValue(value, col.type, currencySymbol),
                    cellStyle: ({ node }) => {
                        const style = {};
                        if (isMetric) style.textAlign = 'right';
                        if (node.rowPinned) style.fontWeight = 600;
                        return style;
                    },
                };
            }),
        [columns, currencySymbol, dimensionWidth]
    );

    const pinnedTopRowData = useMemo(() => {
        if (!hasTotals) return [];
        return [buildPinnedTopRow(columns, totals, translate('STATIC.PAGES.FRAUD.DASHBOARD.TOTAL_ROW'))];
    }, [hasTotals, totals, columns, translate]);

    const tableContent = loading ? (
        <div className={css.sectionBody}>
            <div className={css.spinnerWrapper}>
                <Spinner show />
            </div>
        </div>
    ) : !rawRows.length ? (
        <div className={css.noData}>{translate('STATIC.PAGES.FRAUD.DASHBOARD.NO_DATA')}</div>
    ) : (
        <div className={classNames('ag-theme-quartz', css.gridWrapper)}>
            <AgGridReact
                modules={[AllEnterpriseModule]}
                columnDefs={columnDefs}
                rowData={rawRows}
                pinnedTopRowData={pinnedTopRowData}
                domLayout="autoHeight"
                pagination
                paginationPageSize={pageSize}
                suppressMovableColumns
                suppressDragLeaveHidesColumns
                animateRows={false}
                wrapHeaderText
                autoHeaderHeight
            />
        </div>
    );

    if (naked) return tableContent;
    return (
        <div className={css.sectionCard}>
            <div className={css.sectionHeader}>{title}</div>
            {tableContent}
        </div>
    );
};

DataTable.propTypes = {
    title: PropTypes.string,
    columns: PropTypes.arrayOf(ColumnShape).isRequired,
    data: TableDataShape,
    loading: PropTypes.bool,
    currencySymbol: PropTypes.string,
    translate: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    naked: PropTypes.bool,
    dimensionWidth: PropTypes.number,
};

const NetworkSummarySection = ({
    chartData,
    chartLoading,
    chartTimeBreakdown,
    onChartTimeBreakdownChanged,
    tableData,
    tableLoading,
    currencySymbol,
    translate,
}) => {
    return (
        <div className={css.sectionCard}>
            <div className={css.sectionHeader}>{translate('STATIC.PAGES.FRAUD.DASHBOARD.NETWORK_SUMMARY')}</div>
            {/* Chart wrapper must NOT have overflow-x:auto — that clips Popper dropdowns */}
            <div className={css.chartSectionBody}>
                <div className={css.chartArea}>
                    {chartLoading && (
                        <div className={css.spinnerWrapper}>
                            <Spinner show />
                        </div>
                    )}
                    {!chartLoading && !chartData && (
                        <div className={css.noData}>{translate('STATIC.PAGES.FRAUD.DASHBOARD.NO_DATA')}</div>
                    )}
                    {!chartLoading && chartData && (
                        <LineChart
                            chartData={chartData}
                            chartTimeBreakdown={chartTimeBreakdown}
                            onTimeBreakdownChanged={onChartTimeBreakdownChanged}
                            chart={{ showError: false, lines: 8, selectedMetrics: [] }}
                            numOfTableRows={
                                chartData.dimensionsSorted
                                    ? chartData.dimensionsSorted.length
                                    : Object.keys(chartData.dimensions).length
                            }
                            showLegend
                            showTopLinesInput
                            showMarkers
                        />
                    )}
                </div>
            </div>
            <div className={css.chartTableDivider} />
            <DataTable
                columns={NETWORK_SUMMARY_COLUMNS}
                data={tableData}
                loading={tableLoading}
                currencySymbol={currencySymbol}
                translate={translate}
                naked
            />
        </div>
    );
};

NetworkSummarySection.propTypes = {
    chartData: ChartDataShape,
    chartLoading: PropTypes.bool,
    chartTimeBreakdown: PropTypes.string,
    onChartTimeBreakdownChanged: PropTypes.func,
    tableData: TableDataShape,
    tableLoading: PropTypes.bool,
    currencySymbol: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

const FraudDashboard = ({ translate = () => {} }) => {
    const userCurrency = useSelector(getUserCurrency) || {};
    const currencySymbol = userCurrency.symbol || '$';

    const { startDate, endDate, setBothDates } = useDefaultDates();
    // Fallback dates computed once on mount so the Datepicker renders immediately,
    // before the fetchDefaultDates API call resolves. Ensures react-localize-redux
    // translations are stable when the picker first appears.
    const [fallbackDates] = useState(() => ({
        start: moment()
            .subtract(6, 'days')
            .format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
    }));
    const [chartTimeBreakdown, setChartTimeBreakdown] = useState('day');

    const totalsState = useTotalsData(startDate, endDate);
    const networkSummaryState = useNetworkSummaryData(startDate, endDate);
    const networkChartState = useNetworkChartData(startDate, endDate, chartTimeBreakdown);
    const rejectionMethodsState = useRejectionMethodsData(startDate, endDate);

    const chartData = useMemo(() => buildFraudChartData(networkChartState.data?.results), [networkChartState.data]);

    // Use setBothDates (single setState call) to avoid two renders when the Datepicker fires
    // onApply through a jQuery/Bootstrap event — React 15 only batches inside synthetic events.
    // Two separate setters would produce an intermediate render with mismatched start/end dates,
    // firing all four data hooks with a bad date pair.
    const handleDateApply = useCallback(
        ({ start_date: pickedStartDate, end_date: pickedEndDate }) => {
            setBothDates(pickedStartDate, pickedEndDate);
        },
        [setBothDates]
    );

    return (
        <div className={css.page}>
            <div className={css.header}>
                <h1 className={css.title}>{translate('STATIC.PAGE_HEADERS.FRAUD_DASHBOARD')}</h1>
                <HelpMenu faqLink="https://support.singular.net/hc/en-us/articles/360003141571-Singular-Fraud-Prevention" />
            </div>

            <div className={css.datePickerRow}>
                <Datepicker
                    id="fraud-dashboard-datepicker"
                    startDate={startDate || fallbackDates.start}
                    endDate={endDate || fallbackDates.end}
                    onApply={handleDateApply}
                />
            </div>

            <MetricCards
                data={totalsState.data}
                loading={totalsState.loading}
                currencySymbol={currencySymbol}
                translate={translate}
            />

            <NetworkSummarySection
                chartData={chartData}
                chartLoading={networkChartState.loading}
                chartTimeBreakdown={chartTimeBreakdown}
                onChartTimeBreakdownChanged={setChartTimeBreakdown}
                tableData={networkSummaryState.data}
                tableLoading={networkSummaryState.loading}
                currencySymbol={currencySymbol}
                translate={translate}
            />

            <DataTable
                title={translate('STATIC.PAGES.FRAUD.DASHBOARD.REJECTION_METHODS')}
                columns={REJECTION_METHODS_COLUMNS}
                data={rejectionMethodsState.data}
                loading={rejectionMethodsState.loading}
                currencySymbol={currencySymbol}
                translate={translate}
                dimensionWidth={500}
            />
        </div>
    );
};

FraudDashboard.propTypes = {
    translate: PropTypes.func,
};

export default withLocalize(FraudDashboard);
